<script setup lang="ts">
import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';
import { useAuth } from '@/hooks/useAuth';

import Button from '@/components/button/Button.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import Dropzone from '@/components/fileUpload/Dropzone.vue';
import FormField from '@/components/formField/FormField.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormTextareaInput from '@/components/formTextareaInput/FormTextareaInput.vue';
import IconButton from '@/components/button/IconButton.vue';
import Indicator from '@/components/button/Indicator.vue';

import LifeRingIcon from '@/icons/line/life-ring.svg';

defineProps<{
  isSmallScreen?: boolean;
}>();

const { user } = useAuth();
const messageStorage = useLocalStorage(`bug-report-message-${user.value?.id}`, '');

const isOpen = ref(false);
const selectedFiles = ref<File[]>([]);

const form = useForm<{
  user_agent: string;
  url: string;
  message: string;
  files: File[];
}>({
  method: 'post',
  url: route('bug-report.store'),
  fields: {
    user_agent: navigator.userAgent,
    url: window.location.href,
    message: messageStorage.value,
    files: []
  },
  hooks: {
    success() {
      form.fields.message = '';
      selectedFiles.value = [];
      isOpen.value = false;
    }
  }
});

const fileErrors = computed(() => {
  const errors = form.errors.files as unknown as string[];
  return Object.values(errors ?? [])
    .map((message) => message)
    .join(', ');
});

watch(
  () => form.fields.message,
  (newValue) => {
    messageStorage.value = newValue;
  }
);
</script>

<template>
  <Indicator v-if="!isSmallScreen" label="" :show="!!form.fields.message">
    <IconButton
      ariaLabel="Submit bug report"
      :size="ButtonSize.md"
      :variant="ButtonVariant.soft"
      :icon="LifeRingIcon"
      :color="ButtonColor.slate"
      @click="isOpen = true"
    />
  </Indicator>
  <Button
    v-else
    :color="ButtonColor.slate"
    :iconLeft="LifeRingIcon"
    :variant="ButtonVariant.invisible"
    @click="isOpen = true"
    >Submit bug report</Button
  >
  <Dialog
    :isOpen="isOpen"
    title="Submit bug report"
    :isLoading="form.processing"
    contentClass="max-w-2xl"
    confirmButtonLabel="Submit bug report"
    cancelButtonLabel="Close"
    footerHelperText="You can close this dialog without losing your changes."
    @onCancel="isOpen = false"
    @onClose="isOpen = false"
    @onConfirm="form.submit"
  >
    <FormRows>
      <FormTextareaInput
        label="Bug report"
        name="message"
        v-model="form.fields.message"
        :error="form.errors.message"
      />
      <FormField label="Attachments" :error="fileErrors">
        <Dropzone
          :modelValue="form.fields.files"
          @update:modelValue="
            (files) => (form.fields.files = Array.isArray(files) ? files : files ? [files] : [])
          "
          @error="(error) => form.setErrors({ files: [error] })"
          :error="fileErrors"
          :processing="form.processing"
          :successful="form.successful"
          :maxFileSize="15"
          multiple
        />
      </FormField>
    </FormRows>
  </Dialog>
</template>
